/**
 * GenerateIdeas.js — Orchestrator
 *
 * This component manages the state and layout for the Idea Generation feature.
 * It delegates rendering to:
 *  - IdeasInput (Top/Left)
 *  - IdeasResults (Right)
 *  - IdeasEmptyState (Center)
 *
 * Refactored to separate business logic from UI.
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert,
  alpha,
} from '@mui/material';

import IdeasInput from './ideas/IdeasInput';
import IdeasResults from './ideas/IdeasResults';
import IdeasEmptyState from './ideas/IdeasEmptyState';

// Common card styling
const CARD_STYLE = {
  p: 3,
  borderRadius: 4,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
  height: '100%',
};

const GenerateIdeas = React.memo(({ onGenerateIdeas, loading, idea, error }) => {
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState(null); // All Platforms by default

  const handleTopicChange = useCallback((e) => {
    setTopic(e.target.value);
  }, []);

  const handlePlatformChange = useCallback((val) => {
    setPlatform(val);
  }, []);

  const handleSubmit = useCallback(() => {
    if (topic.trim() && !loading) {
      onGenerateIdeas(topic, platform);
    }
  }, [topic, platform, loading, onGenerateIdeas]);

  const isValid = topic.trim().length > 2 && !loading;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
          Content Idea Engine
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Generate platform-native content strategies in seconds.
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ px: { xs: 2, md: 4 } }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 3, border: '1px solid rgba(239, 68, 68, 0.2)', background: alpha('#ef4444', 0.05) }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 4,
          justifyContent: idea ? 'flex-start' : 'center',
          alignItems: 'stretch',
        }}>
          {/* Input Panel */}
          <Box sx={{
            flex: idea ? { lg: '0 0 30%', xl: '0 0 25%' } : { lg: '0 0 40%', xl: '0 0 60%' },
            minWidth: { lg: 380 },
            transition: 'all 0.5s ease',
          }}>
            <IdeasInput
              topic={topic}
              onTopicChange={handleTopicChange}
              platform={platform}
              onPlatformChange={handlePlatformChange}
              onSubmit={handleSubmit}
              loading={loading}
              isValid={isValid}
              card={CARD_STYLE}
            />
          </Box>

          {/* Results Panel */}
          <Box sx={{
            flex: idea ? 1 : { lg: '0 0 40%', xl: '0 0 35%' },
            minWidth: { lg: 380 },
            transition: 'all 0.5s ease',
          }}>
            <Paper elevation={0} sx={{
              ...CARD_STYLE,
              minHeight: idea ? 700 : 450,
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              transition: 'all 0.5s ease'
            }}>
              {idea ? (
                <Box sx={{ p: 3 }}>
                  <IdeasResults ideaResult={idea} card={{ p: 0 }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <IdeasEmptyState />
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default GenerateIdeas;